import { get } from 'lodash';
import React, { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { AvoRedApp } from '../../components/Layout/AvoRedApp';
import { incrementCartItemsCount } from '../../features/cart/cartSlice';
import { useCartRefetch } from '../../hooks/useCartRefetch';
import ImageWithFallback from '../../components/ImageWithFallback';
import { ProductCard } from '../../components/ProductCard';
import { addRecentlyViewedProduct, getRecentlyViewedProductsExcluding } from '../../utils/recentlyViewedProducts';
import { Product } from '../../types/ProductType';
import { Toast } from '../../components/Toast';

interface ProductShowProps {
  onAddToCart?: (success: boolean, message: string) => void;
}

const GetProduct = `
query GetProduct ($slug: String!) {
  product (slug: $slug) {
    id
    name
    sku
    slug
    description
    price
    final_price
    main_image_url
    type
    status
    min_order_qty
    min_logo_qty
    delivery_date
    categories {
      id
      name
      slug
    }
    variants {
      id
      sku
      name
      price
      final_price
      qty
      status
      is_available
      stock_status
      attributes
    }
  }
}
`;

const AddToCartMutation = `
    mutation AddToCart(
      $product_id: ID!,
      $variantId: String,
      $quantity: Int!
    ) {
      addToCart(
        product_id: $product_id,
        variant_id: $variantId,
        quantity: $quantity
      ) {
          success
          message
          cart {
            id
            product_id
            variant_id
            name
            quantity
            price
            delivery_date
          }
      }
    }
`;

export const ProductShow = ({ onAddToCart }: ProductShowProps) => {

  let { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
   const [qty, setQty] = useState(1);
   const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
   const [isAddingToCart, setIsAddingToCart] = useState(false);
   const isAuth = useAppSelector((state) => state.userLogin.isAuth)
   const [toast, setToast] = useState<{
     isVisible: boolean;
     message: string;
     type: "success" | "error";
   }>({
     isVisible: false,
     message: "",
     type: "success",
   });

   const handleCloseToast = () => {
     setToast((prev) => ({ ...prev, isVisible: false }));
   };

   const showToast = (success: boolean, message: string) => {
     setToast({
       isVisible: true,
       message,
       type: success ? "success" : "error",
     });
   };

  const formatPrice = (price: number): string => {
    if (!price) return '0';
    return Number(price).toFixed(2).replace(/\.00$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const [, addToCartMutation] = useMutation(AddToCartMutation);
  const { refetchCart } = useCartRefetch();

  const getEffectiveMinOrderQty = (product: any) => {
    const selectedVariant = product.variants?.find((v: any) => v.id === selectedVariantId);
    return selectedVariant?.min_order_qty || product.min_order_qty || 1;
  };


   const addToCartOnClick = async () => {
     if (!data?.product?.id) {
       console.error('Product ID not available');
       return;
     }

     const product = data.product;
     const minOrderQty = getEffectiveMinOrderQty(product);

    if (qty < minOrderQty) {
      const msg = `Số lượng tối thiểu là ${minOrderQty} sản phẩm`;
      showToast(false, msg);
      onAddToCart?.(false, msg);
      return;
    }


    if (!isAuth) {
      try {
        const payload = {
          kind: 'productDetail',
          variables: {
            product_id: data.product.id,
            quantity: qty,
            variant_id: selectedVariantId
          }
        };
        localStorage.setItem('pendingAddToCart', JSON.stringify(payload));
      } catch {}
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    
    if (isAddingToCart) return;
    
    setIsAddingToCart(true);
    const variables: any = {
      product_id: data.product.id,
      quantity: qty
    };
    
    if (selectedVariantId) {
      variables.variantId = selectedVariantId;
    }
    
    try {
      const result = await addToCartMutation(variables);
      if (result.error) {
        console.error('Add to cart error:', result.error);
        return;
      }
      if (result.data?.addToCart?.success) {
        // Update cart items count in Redux state
        dispatch(incrementCartItemsCount(qty));
        
        // Refetch cart items để cập nhật số lượng
        await refetchCart();
        
        navigate('/cart');
      } else {
        console.error('Add to cart failed:', result.data?.addToCart?.message);
      }
    } finally {
      setIsAddingToCart(false);
    }
  }

   const [{ fetching, data }] = useQuery({ query: GetProduct, variables: { slug } });
  const [recentlyViewed, setRecentlyViewed] = useState<Product[]>([]);

  // Save product to recently viewed when data is loaded
  useEffect(() => {
    if (data?.product && slug) {
      const product = data.product;
      addRecentlyViewedProduct({
        slug: product.slug,
        name: product.name,
        main_image_url: product.main_image_url,
        price: product.price,
        final_price: product.final_price,
      });
      
      // Update recently viewed list (excluding current product)
      const viewed = getRecentlyViewedProductsExcluding(slug || '');
      // Convert to Product format for ProductCard component
      const viewedProducts: Product[] = viewed.map(v => ({
        id: v.slug,
        name: v.name,
        slug: v.slug,
        __typename: 'Product',
        type: 'basic' as const,
        image: v.main_image_url,
        main_image_url: v.main_image_url || '',
        price: v.price || 0,
        final_price: v.final_price || v.price || 0,
        min_order_qty: v.min_order_qty || 1,
        min_logo_qty: v.min_logo_qty || 0,
        delivery_date: v.delivery_date || 7,
      }));
      setRecentlyViewed(viewedProducts);
    }
  }, [data, slug]);

  return (
      <AvoRedApp>
        {fetching === true ? (
          <div className="min-h-screen bg-gray-50">
            {/* Breadcrumb Skeleton */}
            <nav className="bg-white shadow-sm border-b border-gray-200">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 items-center space-x-2">
                  <div className="h-4 w-20 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-4 text-gray-300">/</div>
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-4 text-gray-300">/</div>
                  <div className="h-4 w-48 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
            </nav>
            
            {/* Product Skeleton */}
            <section className="bg-gray-50 py-12">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
                  {/* Image Skeleton */}
                  <div className="lg:sticky lg:top-8">
                    <div className="aspect-square bg-gray-200 rounded-2xl animate-pulse"></div>
                  </div>
                  
                  {/* Content Skeleton */}
                  <div className="space-y-6">
                    <div className="space-y-3">
                      <div className="h-8 w-3/4 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-4 w-32 bg-gray-200 rounded animate-pulse"></div>
                      <div className="h-10 w-48 bg-gray-200 rounded animate-pulse"></div>
                    </div>
                    <div className="h-32 bg-gray-200 rounded-lg animate-pulse"></div>
                    <div className="h-24 bg-gray-200 rounded-lg animate-pulse"></div>
                    <div className="h-32 bg-gray-200 rounded-xl animate-pulse"></div>
                    <div className="h-14 bg-gray-200 rounded-lg animate-pulse"></div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        ) : (
          <>
            {/* Breadcrumb Navigation */}
            <nav className="bg-white shadow-sm border-b border-gray-200">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 items-center space-x-2 text-sm overflow-x-auto">
                  <Link 
                    to="/" 
                    className="text-gray-500 hover:text-primary-600 transition-colors duration-200 flex items-center whitespace-nowrap"
                  >
                    <svg 
                      className="w-4 h-4 mr-1.5" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" 
                      />
                    </svg>
                    Trang chủ
                  </Link>
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="text-gray-500 whitespace-nowrap">Sản phẩm</span>
                  <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="text-gray-900 font-semibold truncate max-w-xs md:max-w-md">{get(data, 'product.name', '')}</span>
                </div>
              </div>
            </nav>

            <section className="bg-gray-50 py-8 md:py-12">
              <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="grid gap-8 lg:gap-12 lg:grid-cols-2 lg:items-start">
                  {/* Product Image */}
                  <div className="lg:sticky lg:top-8 lg:z-10">
                    <div className="relative overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-gray-200">
                      <ImageWithFallback
                        alt={get(data, 'product.name')}
                        src={get(data, 'product.main_image_url')}
                        className="w-full h-auto object-cover aspect-square"
                      />
                    </div>
                  </div>
  
                  <div className="lg:sticky lg:top-8 lg:z-10">
                    <div className="space-y-6 lg:space-y-8">
                      {/* Product Info */}
                      <div className="space-y-4">
                        <div>
                          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-3">
                            {get(data, 'product.name', '')}
                          </h1>
                          {get(data, 'product.categories', []).length > 0 && (
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                              <span className="text-gray-500 text-sm">Danh mục:</span>
                              {get(data, 'product.categories', []).map((category: any, index: number) => (
                                <Link
                                  key={category.id}
                                  to={`/category/${category.slug}`}
                                  className="inline-flex items-center px-3 py-1.5 rounded-lg bg-primary-50 text-primary-700 hover:bg-primary-100 text-sm font-medium transition-colors duration-200"
                                >
                                  {category.name}
                                </Link>
                              ))}
                            </div>
                          )}
                          {get(data, 'product.sku') && (
                            <div className="flex items-center space-x-2 text-sm">
                              <span className="text-gray-500">SKU:</span>
                              <span className="font-medium text-gray-700 bg-gray-100 px-2 py-1 rounded">{get(data, 'product.sku')}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Price Display */}
                        <div className="flex flex-wrap items-baseline gap-3 pt-2">
                          {(() => {
                            const product = get(data, 'product');
                            const finalPrice = product?.final_price || product?.price || 0;
                            const originalPrice = product?.price || 0;
                            const hasDiscount = product?.final_price && product?.final_price < product?.price;
                            const discountPercent = hasDiscount ? Math.round(((originalPrice - finalPrice) / originalPrice) * 100) : 0;
                            
                            return (
                              <>
                                <div className="flex items-baseline space-x-2">
                                  <span className="text-4xl md:text-5xl font-bold text-gray-900">
                                    {formatPrice(finalPrice)}
                                  </span>
                                  <span className="text-xl md:text-2xl font-semibold text-gray-600">đ</span>
                                </div>
                                {hasDiscount && (
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xl text-gray-400 line-through">
                                      {formatPrice(originalPrice)} đ
                                    </span>
                                    <span className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white text-sm font-bold rounded-lg shadow-md">
                                      <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.104.34-.136.699-.094 1.038.23.22.43.513.49.91.065.431.079.862.073 1.295a19.743 19.743 0 01-.506 2.75c-.191.35-.431.684-.716 1.003a.75.75 0 01-.833.133l-.884-.35a.75.75 0 10-.515 1.25l.772.306c.282.112.576.185.877.212a.75.75 0 01-.574 1.327l-3.35 1.203a11.25 11.25 0 01-3.85-.88l-.194.048a.75.75 0 01-.233-.958l1.409-2.411a.75.75 0 00-.44-1.023l-1.146-.389a.75.75 0 01.356-1.456l1.718.462 1.108-1.897a.75.75 0 00-.578-1.118l-2.006-.293a.75.75 0 01-.431-1.267l.773-.773a.75.75 0 00-.431-1.267l-1.006-.147a.75.75 0 01-.47-.804l.147-1.006a.75.75 0 00-1.267-.431l-.773.773a.75.75 0 01-1.267-.431l-.147-1.006a.75.75 0 00-1.267-.431l-.773.773a.75.75 0 01-1.267-.431L.879 3.545a.75.75 0 01.431-1.267l1.006-.147a.75.75 0 00.47-.804L1.83.926a.75.75 0 011.267.431l.147 1.006a.75.75 0 001.267.431l.773-.773a.75.75 0 011.267.431l.147 1.006a.75.75 0 001.267.431l.773.773a.75.75 0 011.267.431l1.006.147a.75.75 0 00.47.804l.147 1.006a.75.75 0 01-.431 1.267l-1.006.147a.75.75 0 00-.431 1.267l2.006.293a.75.75 0 01.578 1.118l-1.108 1.897 1.718.462a.75.75 0 01-.356 1.456l-1.146.389a.75.75 0 00.44 1.023l1.409 2.411a.75.75 0 01-.233.958l-.194-.048a11.25 11.25 0 01-3.85.88l-3.35-1.203a.75.75 0 01-.574-1.327c.301-.027.595-.1.877-.212l.772-.306a.75.75 0 10.515-1.25l-.884.35a.75.75 0 01-.833-.133c-.285-.319-.525-.653-.716-1.003a19.743 19.743 0 01-.506-2.75c-.006-.433.008-.864.073-1.295.06-.397.26-.69.49-.91.042-.339.01-.698-.094-1.038a6.515 6.515 0 01-.57-1.116c-.208-.322-.477-.65-.822-.88a1 1 0 00-1.45.385c-.23.345-.614.558-.822.88-.214.33-.403.713-.57 1.116-.104.34-.136.699-.094 1.038.23.22.43.513.49.91.065.431.079.862.073 1.295a19.743 19.743 0 01-.506 2.75c-.191.35-.431.684-.716 1.003a.75.75 0 01-.833.133l-.884-.35a.75.75 0 10-.515 1.25l.772.306c.282.112.576.185.877.212a.75.75 0 01-.574 1.327l-3.35 1.203a11.25 11.25 0 01-3.85-.88l-.194.048a.75.75 0 01-.233-.958l1.409-2.411a.75.75 0 00-.44-1.023l-1.146-.389a.75.75 0 01.356-1.456l1.718.462 1.108-1.897a.75.75 0 00-.578-1.118l-2.006-.293a.75.75 0 01-.431-1.267l.773-.773a.75.75 0 00-.431-1.267l-1.006-.147a.75.75 0 01-.47-.804l.147-1.006a.75.75 0 00-1.267-.431l-.773.773a.75.75 0 01-1.267-.431l-.147-1.006a.75.75 0 00-1.267-.431l-.773.773a.75.75 0 01-1.267-.431L.879 3.545a.75.75 0 01.431-1.267l1.006-.147a.75.75 0 00.47-.804L1.83.926a.75.75 0 011.267.431l.147 1.006a.75.75 0 001.267.431l.773-.773a.75.75 0 011.267.431l.147 1.006a.75.75 0 001.267.431l.773.773a.75.75 0 011.267.431l1.006.147a.75.75 0 00.47.804l.147 1.006a.75.75 0 01-.431 1.267l-1.006.147a.75.75 0 00-.431 1.267l2.006.293a.75.75 0 01.578 1.118l-1.108 1.897 1.718.462a.75.75 0 01-.356 1.456l-1.146.389a.75.75 0 00.44 1.023l1.409 2.411a.75.75 0 01-.233.958l-.194-.048a11.25 11.25 0 01-3.85.88l-3.35-1.203a.75.75 0 01-.574-1.327c.301-.027.595-.1.877-.212l.772-.306a.75.75 0 10.515-1.25l-.884.35a.75.75 0 01-.833-.133c-.285-.319-.525-.653-.716-1.003a19.743 19.743 0 01-.506-2.75c-.006-.433.008-.864.073-1.295.06-.397.26-.69.49-.91.042-.339.01-.698-.094-1.038a6.515 6.515 0 01-.57-1.116c-.208-.322-.477-.65-.822-.88a1 1 0 00-1.45.385c-.23.345-.614.558-.822.88-.214.33-.403.713-.57 1.116-.104.34-.136.699-.094 1.038.23.22.43.513.49.91.065.431.079.862.073 1.295a19.743 19.743 0 01-.506 2.75c-.191.35-.431.684-.716 1.003a.75.75 0 01-.833.133l-.884-.35a.75.75 0 10-.515 1.25l.772.306c.282.112.576.185.877.212a.75.75 0 01-.574 1.327l-3.35 1.203a11.25 11.25 0 01-3.85-.88l-.194.048a.75.75 0 01-.233-.958l1.409-2.411a.75.75 0 00-.44-1.023l-1.146-.389a.75.75 0 01.356-1.456l1.718.462 1.108-1.897a.75.75 0 00-.578-1.118l-2.006-.293a.75.75 0 01-.431-1.267l.773-.773a.75.75 0 00-.431-1.267l-1.006-.147a.75.75 0 01-.47-.804l.147-1.006a.75.75 0 00-1.267-.431l-.773.773a.75.75 0 01-1.267-.431l-.147-1.006a.75.75 0 00-1.267-.431l-.773.773a.75.75 0 01-1.267-.431L.879 3.545a.75.75 0 01.431-1.267l1.006-.147a.75.75 0 00.47-.804L1.83.926a.75.75 0 011.267.431l.147 1.006a.75.75 0 001.267.431l.773-.773a.75.75 0 011.267.431l.147 1.006a.75.75 0 001.267.431l.773.773a.75.75 0 011.267.431l1.006.147a.75.75 0 00.47.804l.147 1.006a.75.75 0 01-.431 1.267l-1.006.147a.75.75 0 00-.431 1.267l2.006.293a.75.75 0 01.578 1.118l-1.108 1.897 1.718.462a.75.75 0 01-.356 1.456l-1.146.389a.75.75 0 00.44 1.023l1.409 2.411a.75.75 0 01-.233.958l-.194-.048a11.25 11.25 0 01-3.85.88l-3.35-1.203a.75.75 0 01-.574-1.327c.301-.027.595-.1.877-.212l.772-.306a.75.75 0 10.515-1.25l-.884.35a.75.75 0 01-.833-.133c-.285-.319-.525-.653-.716-1.003a19.743 19.743 0 01-.506-2.75c-.006-.433.008-.864.073-1.295.06-.397.26-.69.49-.91.042-.339.01-.698-.094-1.038a6.515 6.515 0 01-.57-1.116c-.208-.322-.477-.65-.822-.88a1 1 0 00-1.45.385c-.23.345-.614.558-.822.88-.214.33-.403.713-.57 1.116-.104.34-.136.699-.094 1.038.23.22.43.513.49.91.065.431.079.862.073 1.295a19.743 19.743 0 01-.506 2.75c-.191.35-.431.684-.716 1.003a.75.75 0 01-.833.133l-.884-.35a.75.75 0 10-.515 1.25l.772.306c.282.112.576.185.877.212a.75.75 0 01-.574 1.327l-3.35 1.203a11.25 11.25 0 01-3.85-.88l-.194.048a.75.75 0 01-.233-.958l1.409-2.411a.75.75 0 00-.44-1.023l-1.146-.389a.75.75 0 01.356-1.456l1.718.462 1.108-1.897a.75.75 0 00-.578-1.118l-2.006-.293a.75.75 0 01-.431-1.267l.773-.773a.75.75 0 00-.431-1.267l-1.006-.147a.75.75 0 01-.47-.804l.147-1.006a.75.75 0 00-1.267-.431l-.773.773a.75.75 0 01-1.267-.431l-.147-1.006a.75.75 0 00-1.267-.431l-.773.773a.75.75 0 01-1.267-.431L.879 3.545a.75.75 0 01.431-1.267l1.006-.147a.75.75 0 00.47-.804L1.83.926a.75.75 0 011.267.431l.147 1.006a.75.75 0 001.267.431l.773-.773a.75.75 0 011.267.431l.147 1.006a.75.75 0 001.267.431l.773.773a.75.75 0 011.267.431l1.006.147a.75.75 0 00.47.804l.147 1.006a.75.75 0 01-.431 1.267l-1.006.147a.75.75 0 00-.431 1.267l2.006.293a.75.75 0 01.578 1.118l-1.108 1.897 1.718.462a.75.75 0 01-.356 1.456l-1.146.389a.75.75 0 00.44 1.023l1.409 2.411a.75.75 0 01-.233.958l-.194-.048a11.25 11.25 0 01-3.85.88l-3.35-1.203a.75.75 0 01-.574-1.327c.301-.027.595-.1.877-.212l.772-.306a.75.75 0 10.515-1.25l-.884.35a.75.75 0 01-.833-.133c-.285-.319-.525-.653-.716-1.003a19.743 19.743 0 01-.506-2.75c-.006-.433.008-.864.073-1.295.06-.397.26-.69.49-.91.042-.339.01-.698-.094-1.038a6.515 6.515 0 01-.57-1.116c-.208-.322-.477-.65-.822-.88a1 1 0 00-1.45.385z" clipRule="evenodd" />
                                      </svg>
                                      -{discountPercent}%
                                    </span>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
  
            {/* Variant Selection */}
            {get(data, 'product.variants', []).length > 0 && (
              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
                <label htmlFor="variant" className="block text-sm font-semibold text-gray-900 mb-3">
                  Chọn biến thể
                </label>
                <select
                  id="variant"
                  name="variant"
                  value={selectedVariantId || ''}
                  onChange={(e) => setSelectedVariantId(e.target.value || null)}
                  className="block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 bg-white shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:text-sm transition-all duration-200"
                >
                  <option value="">-- Chọn biến thể --</option>
                  {get(data, 'product.variants', []).map((variant: any) => (
                    <option 
                      key={variant.id} 
                      value={variant.id}
                      disabled={!variant.is_available}
                    >
                      {variant.name || variant.sku} - {formatPrice(variant.final_price || variant.price)} đ
                    </option>
                  ))}
                </select>
                {selectedVariantId && (() => {
                  const selectedVariant = get(data, 'product.variants', []).find((v: any) => v.id === selectedVariantId);
                  if (selectedVariant) {
                    return (
                      <div className="mt-4 p-4 bg-gradient-to-br from-primary-50 to-primary-100 rounded-lg border border-primary-200">
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600 font-medium">SKU:</span>
                            <span className="font-semibold text-gray-900 bg-white px-2 py-1 rounded">{selectedVariant.sku}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600 font-medium">Giá:</span>
                            <span className="font-bold text-lg text-gray-900">
                              {formatPrice(selectedVariant.final_price || selectedVariant.price)} đ
                            </span>
                          </div>
                          {/* {selectedVariant.qty !== undefined && (
                            <div className="flex items-center justify-between pt-2 border-t border-blue-200">
                              <span className="text-gray-600 font-medium">Tồn kho:</span>
                              <span className={`font-semibold px-3 py-1 rounded-full text-sm ${selectedVariant.qty > 0 ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100'}`}>
                                {selectedVariant.qty > 0 ? (
                                  <span className="flex items-center">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    {selectedVariant.qty} sản phẩm
                                  </span>
                                ) : (
                                  <span className="flex items-center">
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    Hết hàng
                                  </span>
                                )}
                              </span>
                            </div>
                          )} */}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            )}

            {/* Quantity Input */}
            <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
              <label htmlFor="qty" className="block text-sm font-semibold text-gray-900 mb-3">
                Số lượng {getEffectiveMinOrderQty(data?.product) > 1 ? `(Tối thiểu: ${getEffectiveMinOrderQty(data?.product)})` : ''}
              </label>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => setQty(Math.max(getEffectiveMinOrderQty(data?.product), qty - 1))}
                  disabled={qty <= getEffectiveMinOrderQty(data?.product)}
                  className="flex items-center justify-center w-12 h-12 rounded-lg border-2 border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-primary-500 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Giảm số lượng"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" />
                  </svg>
                </button>
                <input
                  id="qty"
                  name="qty"
                  onChange={(e) => {
                    const minQty = getEffectiveMinOrderQty(data?.product);
                    const value = parseFloat(e.target.value) || minQty;
                    setQty(Math.max(minQty, Math.floor(value)));
                  }}
                  type="number"
                  value={qty}
                  min={getEffectiveMinOrderQty(data?.product)}
                  className="flex-1 rounded-lg border-2 border-gray-300 px-4 py-3 text-lg font-semibold text-gray-900 bg-white shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 text-center"
                  placeholder="Số lượng"
                />
                <button
                  type="button"
                  onClick={() => setQty(qty + 1)}
                  className="flex items-center justify-center w-12 h-12 rounded-lg border-2 border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-primary-500 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all duration-200 font-bold"
                  aria-label="Tăng số lượng"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Total Price Display */}
            {(() => {
              const getUnitPrice = () => {
                if (selectedVariantId) {
                  const variant = get(data, 'product.variants', []).find((v: any) => v.id === selectedVariantId);
                  return variant ? (variant.final_price || variant.price) : get(data, 'product.final_price', get(data, 'product.price'));
                }
                return get(data, 'product.final_price', get(data, 'product.price'));
              };
              const unitPrice = getUnitPrice();
              const totalPrice = unitPrice * qty;
              return (
                <div className="relative overflow-hidden p-5 bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl border border-primary-100 shadow-sm">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-primary-200 opacity-10 rounded-full -mr-16 -mt-16"></div>
                  <div className="relative">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                        Tổng giá
                      </span>
                      <div className="px-2 py-1 bg-primary-100 rounded-md">
                        <span className="text-xs font-medium text-primary-700">
                          {qty} sản phẩm
                        </span>
                      </div>
                    </div>
                    <div className="flex items-baseline space-x-2">
                      <p className="text-3xl font-bold text-gray-900">
                        {formatPrice(totalPrice)} đ
                      </p>
                    </div>
                    <div className="mt-3 pt-3 border-t border-primary-100">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 font-medium">Đơn giá:</span>
                        <span className="text-gray-800 font-semibold">
                          {formatPrice(unitPrice)} đ
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-gray-600 font-medium">Số lượng:</span>
                        <span className="text-gray-800 font-semibold">
                          × {qty}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
  
            <button
              type="button"
              onClick={addToCartOnClick}
              disabled={
                isAddingToCart || (get(data, 'product.variants', []).length > 0 && !selectedVariantId)
              }
              className="w-full rounded-lg bg-primary-600 px-6 py-3.5 text-base font-semibold text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 shadow-md hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed disabled:hover:bg-gray-400 flex items-center justify-center space-x-2"
            >
              {isAddingToCart ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Đang thêm...</span>
                </>
              ) : get(data, 'product.variants', []).length > 0 && !selectedVariantId ? (
                'Vui lòng chọn biến thể'
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span>Thêm vào giỏ hàng</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

                {/* Product Description */}
                <div className="mt-12 lg:mt-16 pt-8 lg:pt-12 border-t border-gray-300 relative z-0">
                  <div className="bg-white rounded-2xl p-8 lg:p-12 shadow-sm border border-gray-200">
                    <div className="flex items-center mb-8 pb-4 border-b border-gray-200">
                      <svg className="w-6 h-6 text-primary-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <h2 className="text-3xl font-bold text-gray-900">Mô tả sản phẩm</h2>
                    </div>
                    <div className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-headings:font-bold prose-p:text-gray-700 prose-p:leading-relaxed prose-a:text-primary-600 prose-a:font-semibold prose-a:no-underline hover:prose-a:underline prose-strong:text-gray-900 prose-ul:text-gray-700 prose-ol:text-gray-700 prose-li:text-gray-700 [&>iframe]:mt-6 [&>iframe]:aspect-video [&>iframe]:w-full [&>iframe]:rounded-xl [&>img]:rounded-xl [&>img]:shadow-md [&>img]:my-6">
                      {get(data, 'product.description') ? (
                        <div dangerouslySetInnerHTML={{ __html: get(data, 'product.description') }} />
                      ) : (
                        <div className="text-center py-12">
                          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-gray-500 text-lg italic">Chưa có mô tả cho sản phẩm này.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Recently Viewed Products */}
                {recentlyViewed.length > 0 && (
                  <div className="mt-16 lg:mt-20 pt-8 lg:pt-12 border-t border-gray-300">
                    <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-sm border border-gray-200">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center">
                          <svg className="w-6 h-6 text-primary-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">Sản phẩm đã xem</h2>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <div className="flex gap-4 lg:gap-6 min-w-max">
                          {recentlyViewed.map((product) => (
                            <div key={product.slug} className="flex-shrink-0 w-[160px] sm:w-[180px] md:w-[200px]">
                              <ProductCard product={product} showPrice={false} />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                 )}
               </div>
             </section>
           </>
         )}
         {/* Toast Notification */}
         <Toast
           message={toast.message}
           type={toast.type}
           isVisible={toast.isVisible}
           onClose={handleCloseToast}
         />
       </AvoRedApp>
     );
   }
